<template>
  <div
    class="w-full border border-gray-300 dark_border-gray-700 rounded-md p-3 text-sm dark_text-gray-200"
    :class="{
      'bg-green-500/20': state === 'accepted',
      'bg-red-500/20': state === 'rejected',
      'bg-sky-500/20': state === 'pending'
    }"
  >
    <header class="mb-2 flex items-start justify-between gap-2">
      <h4 class="m-0 text-sm text-gray-700 dark_text-gray-200">
        <span class="font-bold">
          {{ statusEmoji[state] }} Patch
          {{ index != null ? `#${index + 1}` : '' }}
        </span>

        <span v-if="screenTitle" class="font-normal">
          &ndash; {{ screenTitle }}</span
        >

        <span
          v-if="!isValid"
          class="bg-red-100 dark_bg-red-900 text-red-700 dark_text-red-200 px-1.5 py-0.5 rounded text-xs ml-2"
          >Invalid Patch</span
        >
      </h4>

      <button
        @click="isCollapsed = !isCollapsed"
        class="p-1 rounded bg-gray-500/0 hover_bg-gray-200 dark_hover_bg-gray-700 transition-all flex-shrink-0"
        :title="isCollapsed ? 'Expand diff' : 'Collapse diff'"
      >
        <Icon
          icon="ph:caret-down"
          class="w-4 h-4 text-gray-600 dark_text-gray-400 transition-transform"
          :class="{ 'rotate-180': !isCollapsed }"
        />
      </button>
    </header>

    <!-- Error state -->
    <div v-if="!isValid && !isCollapsed">
      <p class="text-red-600 dark_text-red-300 text-sm font-mono">
        {{ errorMessage }}
      </p>
      <pre
        class="bg-red-50 dark_bg-red-900 text-red-800 dark_text-red-200 p-2 rounded whitespace-pre-wrap max-h-60 overflow-auto"
        >{{ screenYaml }}
      </pre>
    </div>

    <!-- Valid state -->
    <div v-else-if="!isCollapsed">
      <PrettyDiff :diff="diffString" :name="diffTitle" />
    </div>

    <!-- Actions -->
    <footer class="mt-3 flex justify-end gap-2">
      <label
        v-if="canView && !isFinal"
        :for="`preview-${chunkKey}`"
        class="flex items-center gap-2 me-auto select-none text-gray-700 dark_text-gray-200"
      >
        <input
          :id="`preview-${chunkKey}`"
          type="checkbox"
          v-model="previewChecked"
          :disabled="!isValid"
          title="Apply this change temporarily in the preview"
        />
        <span>Preview changes</span>
      </label>
      <button
        v-if="canView"
        class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
        @click="handleView"
        title="View this screen in the preview"
      >
        View
      </button>

      <template v-if="state === 'accepted'">
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
          @click="screenId && $emit('revert', screenId)"
        >
          Revert
        </button>
      </template>
      <template v-else-if="state === 'rejected'">
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
          @click="$emit('un-reject')"
        >
          Undo Reject
        </button>
      </template>
      <template v-else>
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
          @click="$emit('reject')"
        >
          Reject
        </button>
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-blue-500 dark_bg-blue-600 text-white hover_bg-blue-600 dark_hover_bg-blue-500 transition-colors disabled_opacity-50 disabled_cursor-not-allowed"
          :disabled="!isValid"
          @click="emitAccept"
        >
          Accept
        </button>
      </template>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { Icon } from '@iconify/vue';
// @ts-ignore – Vue SFC import handled by Vite / vue-tsc
import PrettyDiff from './PrettyDiff.vue';
import { findScreenById } from '../../lib/applyPatch';
import { createTwoFilesPatch } from 'diff';
import getPageIdForScreen from '../lib/getPageIdForScreen';
import { removeScreenIndexFromYaml } from '../../lib/removeScreenIndex';
import { registerPreview, unregisterPreview } from '../lib/useActivePreview';

type PatchState = 'accepted' | 'rejected' | 'pending';

const props = withDefaults(
  defineProps<{
    /** YAML or JSON string of the proposed screen object inside <edit> block */
    screenYaml: string;
    /** Full compiled YAML of current draft */
    currentYaml: string;
    index?: number;
    /** Patch state propagated from parent; undefined => pending */
    state?: PatchState;
    /** Globally unique key identifying this chunk */
    chunkKey: string;
    /** Ordering across all chunks in the conversation, for stable composition */
    order: number;
  }>(),
  {
    state: 'pending'
  }
);

const emit = defineEmits<{
  /** Emitted when author clicks Apply */
  (e: 'accept', screenYaml: string): void;
  /** Emitted when author clicks Reject */
  (e: 'reject'): void;
  /** Emitted when author clicks View - contains pageId and screenId */
  (e: 'view', pageId: string, screenId: string): void;
  /** Finalized states actions */
  (e: 'revert', screenId: string): void;
  (e: 'un-reject'): void;
}>();

const isValid = ref(true);
const errorMessage = ref('');
const parsedScreen = ref<any>(null);

const previewChecked = ref(true);
const isPreviewed = ref(false);
const lastPreviewedId = ref<string | null>(null);

// Collapsed state: default expanded for pending, collapsed for accepted/rejected
const isCollapsed = ref(props.state !== 'pending');

/**
 * Extract screen ID from the proposed screen definition
 */
const screenId = computed(() =>
  isValid.value && parsedScreen.value?.id ? parsedScreen.value.id : null
);

/**
 * Whether we can show the View button (true if we have a screen ID)
 */
const canView = computed(() => screenId.value !== null);

/**
 * Title for the diff header based on patch state
 */
const diffTitle = computed(() => {
  if (props.state === 'accepted') return 'Accepted Changes';
  if (props.state === 'rejected') return 'Rejected Changes';
  return 'Proposed Changes';
});

/** Whether patch is finalized (accepted or rejected) */
const isFinal = computed(
  () => props.state === 'accepted' || props.state === 'rejected'
);

/**
 * Handle the View button click
 */
const handleView = () => {
  if (!screenId.value) return;

  const currentObj = yamlLoad(props.currentYaml) as any;
  const pageId = getPageIdForScreen(screenId.value, currentObj);

  if (pageId) {
    emit('view', pageId, screenId.value);
  }
};

// Validate YAML & cache parsed content
const validate = () => {
  try {
    const raw = props.screenYaml.trim();
    let parsed: any;

    // Detect JSON vs YAML – simple heuristic: starts with { or [
    if (raw.startsWith('{') || raw.startsWith('[')) {
      parsed = JSON.parse(raw);
    } else {
      parsed = yamlLoad(raw);
    }

    parsedScreen.value = parsed;
    isValid.value = true;
    errorMessage.value = '';
  } catch (err: any) {
    isValid.value = false;
    errorMessage.value = err.message || 'Unknown parse error';
  }
};

watch(() => props.screenYaml, validate, { immediate: true });

// Update collapsed state when status changes
watch(
  () => props.state,
  newState => {
    // Auto-collapse when patch is accepted or rejected, expand when pending
    isCollapsed.value = newState !== 'pending';
  }
);

const screenTitle = computed<string>(() => {
  const s = parsedScreen.value as UcScreen;
  if (s.title) return s.title;
  if (s.heading) return s.heading;
  if (s.subheading) return s.subheading;
  return '';
});

// Compute diff string when valid – compares original screen vs merged screen
const diffString = computed(() => {
  if (!isValid.value || !screenId.value) {
    return '';
  }

  try {
    const currentObj = yamlLoad(props.currentYaml) as any;
    const originalScreen = findScreenById(currentObj as any, screenId.value);

    if (!originalScreen) return '';

    const proposedScreen = parsedScreen.value;

    const currentYaml = yamlDump(originalScreen, { lineWidth: -1 });
    const proposedYaml = yamlDump(proposedScreen, { lineWidth: -1 });

    // Strip screenIndex lines from current YAML only to avoid diff noise
    const cleanedCurrentYaml = removeScreenIndexFromYaml(currentYaml);

    return createTwoFilesPatch(
      'Current',
      'Proposed',
      cleanedCurrentYaml,
      proposedYaml
    );
  } catch {
    return '';
  }
});

function emitAccept() {
  // Emit only if considered valid
  if (!isValid.value) return;

  try {
    const yamlString = yamlDump(parsedScreen.value, { lineWidth: -1 });
    emit('accept', yamlString);
  } catch {
    // Fallback to original content
    emit('accept', props.screenYaml);
  }
}

const dumpParsed = () => {
  try {
    return yamlDump(parsedScreen.value, { lineWidth: -1 });
  } catch {
    return props.screenYaml;
  }
};

watch(
  [
    previewChecked,
    isValid,
    screenId,
    () => parsedScreen.value,
    () => props.state
  ],
  ([checked, valid, id, _parsed, state]) => {
    const active =
      state !== 'rejected' && state !== 'accepted' && checked && valid && !!id;
    if (!active) {
      unregisterPreview(props.chunkKey);
      isPreviewed.value = false;
      lastPreviewedId.value = null;
      return;
    }

    registerPreview(props.chunkKey, {
      screenId: id as string,
      order: props.order,
      edit: { type: 'patch', screenYaml: dumpParsed() }
    });
    isPreviewed.value = true;
    lastPreviewedId.value = id as string;
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  unregisterPreview(props.chunkKey);
});

const statusEmoji = {
  accepted: '✅',
  rejected: '❌',
  pending: '⏳'
};
</script>
